import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from "react-router-dom";

import {
  Box,
  Button,
  TextField,
  Typography,
  CircularProgress,
  Divider,
  MenuItem,
  IconButton,
  Card,
  CardContent,
  CardHeader,
  Grid,
  Paper,
  Alert,
  Chip,
  Fade,
  Grow,
  InputAdornment,
} from '@mui/material';
import {
  Add,
  Image,
  Delete,
  Inventory,
  Category as CategoryIcon,
  Save,
  ArrowUpward,
  ArrowDownward,
} from '@mui/icons-material';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import {
  handleNumericChange,
  handleNumericFocus,
  handleNumericBlur,
} from "../../utils/numericInput";

import { uploadImage, upsertProductOptionValueMeta } from '../../api/superAdmin';
import {
  addProduct,
  updateProduct,
  fetchProductById,
  fetchLocations,
  fetchCategories,
  addCategory,
} from '../../api/superAdmin';

import VariantMatrix from './VariantMatrix';
import AddVariantModal from './AddVariantModal';
import VariantCard from './VariantCard';
import OptionInput from './OptionInput';

import type {
  AdminProductResponse,
  AdminProductPayload,
  WholesaleBreak,
} from '../../api/superAdmin';
import type { Option, Variant, StockLocation, Category } from '../../types/product';
import NumericTextField from '../inputs/NumericTextField';
import MediaLibraryModal from '../media/MediaLibraryModal';
import WholesaleBreaksEditor from './WholesaleBreaksEditor';

interface ProductFormProps {
  isEditMode?: boolean;
  productId?: number;
  onSubmitSuccess: () => void;
}

// Helper functions
const norm = (s: string) => s.trim().toLowerCase().replace(/\s+/g, " ");

const canon = (s: string) => (s || "").toLowerCase().replace(/[^a-z0-9]/g, "");
const buildLooseRegex = (value: string) => {
  const parts = (value || "").trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return null;

  const first = parts[0];
  const firstPattern =
    first.length <= 3
      ? first.split("").map(ch => `${ch}[\\W_]*`).join("").replace(/[\\W_]*$/, "")
      : first;

  const rest = parts.slice(1).join("[\\W_]*");
  const pattern = rest ? `${firstPattern}[\\W_]*${rest}` : firstPattern;

  return new RegExp(pattern, "i");
};

const variantKeyFromAttrs = (attrs: { name: string; value: string }[]) =>
  attrs
    .filter(a => a?.name && a?.value)
    .map(a => `${norm(a.name)}=${norm(a.value)}`)
    .sort()
    .join("|");

const makeAttrs = (pairs: { name: string; value: string }[]) =>
  pairs
    .filter(p => p.name && p.value)
    .map(p => ({ name: p.name.trim(), value: p.value.trim() }))
    .sort((a, b) => a.name.localeCompare(b.name));

const defaultLocationsForVariant = (allLocations: StockLocation[]) => {
  const defaultStocks = [
    { stock_type: "in_store" as const, stock: 0 },
    { stock_type: "online" as const, stock: 0 },
  ];
  return allLocations.map(loc => ({
    name: loc.name,
    stocks: defaultStocks.map(s => ({ ...s })),
  }));
};

const slug = (s: string) =>
  (s || "")
    .trim()
    .toLowerCase()
    .replace(/&/g, "and")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .replace(/--+/g, "-");

const buildVariantSku = (baseSku: string, attrs: { name: string; value: string }[]) => {
  if (attrs.length === 0) return baseSku;

  const sorted = [...attrs].sort((a, b) => a.name.localeCompare(b.name));
  const suffix = sorted.map(a => slug(a.value)).filter(Boolean).join("-");

  return suffix ? `${baseSku}-${suffix}` : baseSku;
};

// Image upload validation
const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
const MAX_IMAGE_SIZE = 8 * 1024 * 1024; // 8MB
const validateImageFile = (file: File): string | null => {
  if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
    return 'Please select a valid image file (JPEG, PNG, WebP, or GIF).';
  }
  if (file.size > MAX_IMAGE_SIZE) {
    return 'Image is too large. Maximum size is 8MB.';
  }
  return null;
};

// ✅ OPTIMIZATION 1: Custom debounce hook
const useDebounce = <T,>(value: T, delay: number): T => {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

const ProductForm: React.FC<ProductFormProps> = ({
  isEditMode = false,
  productId,
  onSubmitSuccess,
}) => {
  // Form state
  const [productName, setProductName] = useState('');
  const [sku, setSku] = useState('');
  const [price, setPrice] = useState<number | ''>('');
  const [supplierPrice, setSupplierPrice] = useState<number | ''>('');
  const [wholesaleBreaks, setWholesaleBreaks] = useState<WholesaleBreak[]>([]);
  const [breaksHydrateKey, setBreaksHydrateKey] = useState(0);
  const [availability, setAvailability] = useState<'both' | 'wholesale_only' | 'retail_only'>('both');
  const [backorderPolicy, setBackorderPolicy] = useState<'inherit' | 'none' | 'wholesale_only' | 'both'>('inherit');
  const [description, setDescription] = useState('');
  const [image, setImage] = useState<File | null>(null);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [removeMainImage, setRemoveMainImage] = useState(false);
  const [mediaOpen, setMediaOpen] = useState(false);
  const [selectedVariantIndex, setSelectedVariantIndex] = useState<number | null>(null);

  // Options & Variants
  const [options, setOptions] = useState<Option[]>([]);
  const [variants, setVariants] = useState<Variant[]>([]);
  const [locations, setLocations] = useState<StockLocation[]>([]);

  // Categories
  const [categories, setCategories] = useState<Category[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<number | null>(null);
  const [customCategory, setCustomCategory] = useState('');

  // UI State
  const [loading, setLoading] = useState(false);
  const [fillAllInStore, setFillAllInStore] = useState<number>(0);
  const [fillAllOnline, setFillAllOnline] = useState<number>(0);
  const [addVariantModalOpen, setAddVariantModalOpen] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const [optionValueMeta, setOptionValueMeta] = useState<
    Array<{ option_name: string; option_value: string; description: string | null }>
  >([]);

  const [effectiveProductId, setEffectiveProductId] = useState<number | null>(productId ?? null);
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);

  const navigate = useNavigate();
  const location = useLocation();

  const isEditingNow = isEditMode || !!effectiveProductId;

  // ✅ OPTIMIZATION 2: Debounce options with longer delay
  const debouncedOptions = useDebounce(options, 500);

  // ✅ OPTIMIZATION 3: Memoized variant map
  const variantsByKey = useMemo(() => {
    const map = new Map<string, Variant>();
    for (const v of variants) {
      map.set(variantKeyFromAttrs(makeAttrs(v.attributes)), v);
    }
    return map;
  }, [variants]);

  // Calculate markup and margin
  const markup = useMemo(() => {
    if (price && supplierPrice && Number(supplierPrice) > 0) {
      return ((Number(price) - Number(supplierPrice)) / Number(supplierPrice)) * 100;
    }
    return 0;
  }, [price, supplierPrice]);

  const margin = useMemo(() => {
    if (price && supplierPrice && Number(price) > 0) {
      return ((Number(price) - Number(supplierPrice)) / Number(price)) * 100;
    }
    return 0;
  }, [price, supplierPrice]);

  // Fetch initial data
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const [locationsData, categoriesData] = await Promise.all([
          fetchLocations(),
          fetchCategories()
        ]);

        setLocations(locationsData.locations || []);

        const backendCategories: Category[] = categoriesData.categories.map((cat: any) => ({
          id: cat.id,
          name: cat.name,
        }));

        setCategories([{ id: null, name: 'Uncategorized' }, ...backendCategories]);

        const idToLoad = effectiveProductId ?? productId;
        if ((isEditMode || !!effectiveProductId) && idToLoad) {
          const product: AdminProductResponse = await fetchProductById(idToLoad);

          setProductName(product.product_name);
          setSku(product.sku);
          setPrice(product.price);
          setSupplierPrice(product.supplier_price);
          setWholesaleBreaks(
            product.wholesale_breaks && product.wholesale_breaks.length
              ? product.wholesale_breaks
              : product.wholesale_price != null
                ? [{ min_qty: product.wholesale_min_qty || 1, price: product.wholesale_price }]
                : []
          );
          setBreaksHydrateKey((k) => k + 1);
          setAvailability(product.availability ?? 'both');
          setBackorderPolicy(product.backorder_policy ?? 'inherit');
          setDescription(product.description || '');
          setImageUrl(product.image_url ?? null);
          setRemoveMainImage(false);

          const matchingCategory = backendCategories.find((cat) => cat.name === product.category);
          setSelectedCategory(matchingCategory?.id || null);
          setOptionValueMeta(product.option_value_meta || []);

          const processedVariants = product.variants.map((variant) => ({
            sku: variant.sku,
            price: variant.price,
            // Variant's OWN wholesale override (admin GET returns own-only, so an
            // empty schedule means the variant inherits the product's pricing).
            wholesale_breaks:
              variant.wholesale_breaks && variant.wholesale_breaks.length
                ? variant.wholesale_breaks
                : variant.wholesale_price != null
                  ? [{ min_qty: variant.wholesale_min_qty || 1, price: variant.wholesale_price }]
                  : [],
            locations: (variant.locations || []).reduce((acc: StockLocation[], loc) => {
              const existing = acc.find((l) => l.name === loc.location_name);
              if (existing) {
                existing.stocks.push({ stock_type: loc.stock_type, stock: loc.stock });
              } else {
                acc.push({
                  name: loc.location_name,
                  stocks: [{ stock_type: loc.stock_type, stock: loc.stock }],
                });
              }
              return acc;
            }, []),
            attributes: makeAttrs(variant.attributes || []),
            imageUrl: variant.image_url ?? null,
            removeImage: false,
          }));

          setVariants(processedVariants);
          setOptions(product.option_groups || []);
        }
      } catch (err) {
        console.error('Error fetching data:', err);
      }
    };

    fetchInitialData();
  }, [isEditMode, productId, effectiveProductId]);

  // Generate a default SKU once for new products (only if the user hasn't set one).
  // Leaving `sku` untouched afterwards lets admins enter a custom SKU in create mode.
  useEffect(() => {
    if (!isEditingNow && productName && !sku) {
      const baseSku = "fc";
      setSku(`${baseSku}-${Date.now()}`);
    }
  }, [productName, isEditingNow, sku]);

  // Validate form
  const validateForm = useCallback(() => {
    const newErrors: Record<string, string> = {};

    if (!productName.trim()) {
      newErrors.productName = 'Product name is required';
    }

    if (!sku.trim()) {
      newErrors.sku = 'SKU is required';
    }

    if (!price || Number(price) <= 0) {
      newErrors.price = 'Retail price must be greater than 0';
    }

    if (!supplierPrice || Number(supplierPrice) <= 0) {
      newErrors.supplierPrice = 'Supplier price must be greater than 0';
    }

    if (options.length > 0) {
      options.forEach((option, idx) => {
        if (!option.name.trim()) {
          newErrors[`option_${idx}`] = 'Option name is required';
        }
        if (option.values.length === 0) {
          newErrors[`option_values_${idx}`] = 'At least one value is required';
        }
      });
    }

    if (variants.length === 0) {
      newErrors.variants = 'At least one variant is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [productName, sku, price, supplierPrice, options, variants]);

  // ✅ OPTIMIZATION 4: Variant operations with proper normalization
  const upsertVariant = useCallback((attrs: { name: string; value: string }[]) => {
    const clean = makeAttrs(attrs);
    const key = variantKeyFromAttrs(clean);

    if (variantsByKey.has(key)) return;

    let candidateSku = buildVariantSku(sku, clean);
    let counter = 2;
    while (variants.some(v => v.sku === candidateSku)) {
      candidateSku = `${buildVariantSku(sku, clean)}-${counter++}`;
    }

    const newVariant: Variant = {
      sku: candidateSku,
      price: Number(price) || 0,
      locations: defaultLocationsForVariant(locations),
      attributes: clean,
      imageUrl: undefined,
    };

    setVariants(prev => [...prev, newVariant]);
  }, [sku, price, locations, variantsByKey, variants]);

  // ✅ OPTIMIZATION 5: Only recalculate variants when options change (debounced)
  useEffect(() => {
    const keyOf = (attrs: { name: string; value: string }[]) =>
      variantKeyFromAttrs(makeAttrs(attrs));

    if (!sku || locations.length === 0) return;

    // 0 options => only auto-create on create-mode
    if (debouncedOptions.length === 0) {
      if (isEditingNow) return;
      setVariants((prev) => {
        if (prev.length > 0) return prev;
        return [{
          sku,
          price: Number(price) || 0,
          locations: defaultLocationsForVariant(locations),
          attributes: [],
          imageUrl: undefined,
        }];
      });
      return;
    }

    // 1 option => auto-sync
    if (debouncedOptions.length === 1) {
      const opt = debouncedOptions[0];
      const optName = (opt?.name || "").trim();
      const vals = (opt?.values || []).map(v => v.trim()).filter(Boolean);
      if (!optName || vals.length === 0) return;

      const desiredAttrs = vals.map(v => [{ name: optName, value: v }]);
      const desiredKeys = new Set(desiredAttrs.map(a => keyOf(a)));

      setVariants((prev) => {
        const kept = prev.filter(v => desiredKeys.has(keyOf(v.attributes || [])));

        const existingByKey = new Map<string, Variant>();
        for (const v of kept) existingByKey.set(keyOf(v.attributes || []), v);

        const next = [...kept];
        const usedSkus = new Set(next.map(v => v.sku));

        const makeUniqueSku = (base: string) => {
          let candidate = base;
          let counter = 2;
          while (usedSkus.has(candidate)) candidate = `${base}-${counter++}`;
          usedSkus.add(candidate);
          return candidate;
        };

        for (const attrs of desiredAttrs) {
          const clean = makeAttrs(attrs);
          const k = keyOf(clean);
          if (existingByKey.has(k)) continue;

          const base = buildVariantSku(sku, clean);
          next.push({
            sku: makeUniqueSku(base),
            price: Number(price) || 0,
            locations: defaultLocationsForVariant(locations),
            attributes: clean,
            imageUrl: undefined,
          });
        }

        return next;
      });

      return;
    }
  }, [debouncedOptions, sku, price, locations, isEditingNow]); // Removed price from dependencies

  // ✅ OPTIMIZATION 6: Memoized callbacks for option operations
  const removeVariantByAttrs = useCallback((attrs: { name: string; value: string }[]) => {
    const key = variantKeyFromAttrs(makeAttrs(attrs));
    setVariants(prev =>
      prev.filter(v => variantKeyFromAttrs(makeAttrs(v.attributes)) !== key)
    );
  }, []);

  const toggleVariant = useCallback((attrs: { name: string; value: string }[]) => {
    const key = variantKeyFromAttrs(makeAttrs(attrs));
    const exists = variantsByKey.has(key);

    if (exists) {
      removeVariantByAttrs(attrs);
    } else {
      upsertVariant(attrs);
    }
  }, [variantsByKey, removeVariantByAttrs, upsertVariant]);

  const updateVariant = useCallback((index: number, updatedVariant: Variant) => {
    setVariants(prev => {
      const newVariants = [...prev];
      newVariants[index] = {
        ...updatedVariant,
        attributes: makeAttrs(updatedVariant.attributes),
      };
      return newVariants;
    });
  }, []);

  const deleteVariant = useCallback((index: number) => {
    setVariants(prev => prev.filter((_, i) => i !== index));
  }, []);

  const handleVariantImageUpload = useCallback(async (index: number, file: File) => {
    const validationError = validateImageFile(file);
    if (validationError) {
      alert(validationError);
      return;
    }
    try {
      const uploadedUrl = await uploadImage(file);
      // Reuse the uploaded URL at submit; don't retain the File (avoids re-upload).
      setVariants(prev => {
        const updated = [...prev];
        updated[index] = {
          ...prev[index],
          imageFile: undefined,
          imageUrl: uploadedUrl,
          removeImage: false,
        };
        return updated;
      });
    } catch (err) {
      console.error('Failed to upload variant image:', err);
      alert('Failed to upload variant image. Please try again.');
    }
  }, []);

  const handleVariantImageRemove = useCallback((index: number) => {
    setVariants(prev => {
      const updated = [...prev];
      updated[index] = {
        ...prev[index],
        imageFile: undefined,
        imageUrl: null,
        removeImage: true,
      };
      return updated;
    });
  }, []);

  const handleApplyStockToAllVariants = useCallback(() => {
    setVariants(prev => prev.map(variant => ({
      ...variant,
      locations: variant.locations.map(loc => ({
        ...loc,
        stocks: loc.stocks.map(stockEntry => {
          if (stockEntry.stock_type === 'in_store') {
            return { ...stockEntry, stock: fillAllInStore };
          } else if (stockEntry.stock_type === 'online') {
            return { ...stockEntry, stock: fillAllOnline };
          }
          return stockEntry;
        }),
      })),
    })));
  }, [fillAllInStore, fillAllOnline]);

  const moveOption = useCallback((fromIndex: number, toIndex: number) => {
    setOptions((prev) => {
      if (toIndex < 0 || toIndex >= prev.length) return prev;

      const next = [...prev];
      const [moved] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, moved);

      return next;
    });
  }, []);

  // ✅ OPTIMIZATION 7: Memoized option handlers
  const handleOptionNameChange = useCallback((index: number, newName: string) => {
    const newTrim = newName.trim();
    const newNorm = norm(newTrim);

    setOptions(prev => {
      const oldName = prev[index].name;
      const oldNorm = norm(oldName);

      const updatedOptions = [...prev];
      updatedOptions[index] = { ...updatedOptions[index], name: newName };

      // ✅ move variant sync INSIDE so it's always in sync
      if (oldNorm && newNorm && oldNorm !== newNorm) {
        setVariants(prevVariants =>
          prevVariants.map(v => ({
            ...v,
            attributes: makeAttrs(
              (v.attributes || []).map(a =>
                norm(a.name) === oldNorm ? { ...a, name: newTrim } : a
              )
            ),
          }))
        );
      }

      return updatedOptions;
    });
  }, []);

  const handleOptionValuesChange = useCallback((index: number, newValues: string[]) => {
    setOptions(prev =>
      prev.map((option, optionIndex) =>
        optionIndex === index
          ? { ...option, values: newValues }
          : option
      )
    );
  }, []);

  const handleOptionValueRename = useCallback((index: number, oldVal: string, newVal: string) => {
    const optName = options[index].name;
    if (!optName) return;

    applyOptionValueRenameToVariants(optName, oldVal, newVal);
  }, [options, sku]);

  const handleOptionValueDelete = useCallback((index: number, val: string) => {
    const optName = options[index].name;

    setVariants(prev =>
      prev.filter(v => {
        const usesDeleted = (v.attributes || []).some(
          a => norm(a.name) === norm(optName) && norm(a.value) === norm(val)
        );
        return !usesDeleted;
      })
    );
  }, [options]);

  const handleOptionDelete = useCallback((index: number) => {
    const optName = norm(options[index].name);

    setOptions(prev => prev.filter((_, i) => i !== index));

    setVariants(prev =>
      prev
        .filter(v => !(v.attributes || []).some(a => norm(a.name) === optName))
        .filter(v => (v.attributes || []).length > 0)
    );
  }, [options]);

  const handleAddCustomCategory = async () => {
    if (customCategory.trim() && !categories.some((cat) => cat.name === customCategory)) {
      try {
        const newCategory = await addCategory(customCategory);
        const newCategoryObj = { id: newCategory.id, name: newCategory.name };
        setCategories([...categories, newCategoryObj]);
        setSelectedCategory(newCategory.id);
        setCustomCategory('');
      } catch (err) {
        console.error('Error adding category:', err);
        alert('Failed to add category. Please try again.');
      }
    }
  };

  const handleMainImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const validationError = validateImageFile(file);
      if (validationError) {
        alert(validationError);
        return;
      }
      setImage(file);
      setRemoveMainImage(false);
      try {
        const uploadedImageUrl = await uploadImage(file);
        setImageUrl(uploadedImageUrl);
        // Already uploaded for preview; clear the File so submit reuses the URL
        // instead of re-uploading (avoids duplicate storage objects).
        setImage(null);
      } catch (err) {
        console.error('Failed to upload main image:', err);
        alert('Failed to upload main image. Please try again.');
      }
    }
  };

  const handleMainImageRemove = () => {
    setImage(null);
    setImageUrl(null);
    setRemoveMainImage(true);
  };

  const applyOptionValueRenameToVariants = useCallback(
    (optName: string, oldVal: string, newVal: string) => {
      const oldTrim = oldVal.trim();
      const newTrim = newVal.trim();

      const oldSlug = slug(oldTrim);
      const newSlug = slug(newTrim);

      setVariants((prev) =>
        prev.map((v) => {
          const oldAttrs = makeAttrs(v.attributes || []);

          const newAttrs = makeAttrs(
            (v.attributes || []).map((a) => {
              if (norm(a.name) !== norm(optName)) return a;
              if (norm(a.value) !== norm(oldTrim)) return a;
              return { ...a, value: newTrim };
            })
          );

          const expectedOldSku = buildVariantSku(sku, oldAttrs);
          const expectedNewSku = buildVariantSku(sku, newAttrs);

          const stripCounter = (s: string) => s.replace(/-\d+$/, "");
          const suffixMatch = v.sku.match(/-\d+$/)?.[0] || "";

          const currentBase = stripCounter(v.sku);

          const isAuto =
            currentBase === expectedOldSku ||
            currentBase === sku ||
            currentBase.startsWith(`${sku}-`);

          if (isAuto) {
            return { ...v, sku: `${expectedNewSku}${suffixMatch}`, attributes: newAttrs };
          }

          const rx = oldSlug
            ? new RegExp(`(^|-)${oldSlug}(-|$)`, "g")
            : null;

          if (rx && rx.test(v.sku)) {
            const nextSku = v.sku.replace(rx, `$1${newSlug}$2`);
            return { ...v, sku: nextSku, attributes: newAttrs };
          }

          return { ...v, attributes: newAttrs };
        })
      );
    },
    [sku]
  );

  type SubmitMode = "save" | "save_close";

  const handleSubmit = async (mode: SubmitMode) => {
    if (!validateForm()) {
      const firstError = document.querySelector('.Mui-error');
      firstError?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    setLoading(true);

    try {
      let mainImageUrl: string | null | undefined = undefined;

      if (removeMainImage) {
        mainImageUrl = null;
      } else if (image instanceof File) {
        mainImageUrl = await uploadImage(image);
      } else {
        mainImageUrl = imageUrl ?? undefined;
      }

      const variantsWithImages = await Promise.all(
        variants
          .filter((v) => !v.markedForDelete)
          .map(async (variant) => {
            let variantImageUrl: string | null | undefined = undefined;

            if (variant.removeImage || variant.imageUrl === null) {
              variantImageUrl = null;
            } else if (variant.imageFile) {
              variantImageUrl = await uploadImage(variant.imageFile);
            } else {
              variantImageUrl = variant.imageUrl ?? undefined;
            }

            const variantName = variant.attributes.map((a) => a.value).join(" ") || variant.sku;

            return {
              sku: variant.sku,
              price: variant.price,
              // Empty array clears the variant override → inherits product pricing.
              wholesale_breaks: variant.wholesale_breaks ?? [],
              ...(typeof variantImageUrl !== "undefined" ? { image_url: variantImageUrl } : {}),
              variant_name: variantName,
              attributes: variant.attributes.map(a => ({ name: a.name, value: a.value })),
              locations: variant.locations,
            };
          })
      );

      // ✅ FORCE SYNC latest option names before submit
      const finalOptions = options.map(o => ({
        ...o,
        name: (o.name || "").trim(),
      }));

      const productPayload: AdminProductPayload = {
        name: productName,
        sku,
        price: Number(price) || 0,
        supplier_price: Number(supplierPrice) || 0,
        wholesale_breaks: wholesaleBreaks,
        availability,
        backorder_policy: backorderPolicy,
        description: description || undefined,
        category_id: selectedCategory,
        ...(typeof mainImageUrl !== "undefined" ? { image_url: mainImageUrl } : {}),
        option_order: finalOptions
          .map((o) => o.name)
          .filter(Boolean),
        variants: variantsWithImages,
      };

      const idToUpdate = effectiveProductId ?? productId;

      if ((isEditMode || !!effectiveProductId) && idToUpdate) {
        await updateProduct(idToUpdate, productPayload);
        setLastSavedAt(new Date());
      } else {
        const created = await addProduct(productPayload);
        const newId = created.productId;

        setEffectiveProductId(newId);
        setLastSavedAt(new Date());

        if (!isEditMode && location.pathname === "/products/add") {
          navigate(`/products/edit/${newId}`, { replace: true });
        }
      }

      setSaveError(null);

      if (mode === "save_close") {
        onSubmitSuccess();
      }
    } catch (error) {
      console.error("Error submitting product:", error);
      setSaveError("Failed to save product. Please try again.");
      alert("Failed to save product. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveValueMeta = useCallback(async (args: {
    productId: number;
    option_name: string;
    option_value: string;
    description: string | null;
    old_option_value?: string;
  }) => {
    setOptionValueMeta((prev) => {
      const next = [...prev];

      if (args.old_option_value) {
        const oi = next.findIndex(
          (m) =>
            norm(m.option_name) === norm(args.option_name) &&
            norm(m.option_value) === norm(args.old_option_value!)
        );
        if (oi !== -1) next.splice(oi, 1);
      }

      const i = next.findIndex(
        (m) =>
          norm(m.option_name) === norm(args.option_name) &&
          norm(m.option_value) === norm(args.option_value)
      );

      const row = {
        option_name: args.option_name,
        option_value: args.option_value,
        description: args.description,
      };

      if (i === -1) next.push(row);
      else next[i] = row;

      return next;
    });

    await upsertProductOptionValueMeta(args.productId, {
      option_name: args.option_name,
      option_value: args.option_value,
      description: args.description,
    });
  }, []);

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
      {/* Header */}
      <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mb: 4
      }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 600, mb: 0.5 }}>
            {isEditingNow ? 'Edit Product' : 'Add Product'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {isEditMode ? 'Update your product information' : 'Create a new product listing'}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          <Button
            variant="outlined"
            onClick={() => handleSubmit("save")}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={18} /> : <Save />}
          >
            Save
          </Button>

          <Button
            variant="contained"
            onClick={() => handleSubmit("save_close")}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : <Save />}
            sx={{
              px: 4,
              py: 1,
              boxShadow: "none",
              "&:hover": { boxShadow: "none" },
            }}
          >
            Save & Close
          </Button>
        </Box>
      </Box>
      {saveError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {saveError}
        </Alert>
      )}

      {lastSavedAt && !saveError && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Saved at {lastSavedAt.toLocaleTimeString()}
        </Alert>
      )}

      {/* Main Content */}
      <Grid container spacing={3}>
        {/* Left Column - Basic Info */}
        <Grid item xs={12} lg={8}>
          {/* Basic Information Card */}
          <Card sx={{ mb: 3, borderRadius: 2 }}>
            <CardHeader
              title="Basic Information"
              sx={{
                borderBottom: '1px solid',
                borderColor: 'divider',
                bgcolor: 'action.hover',
              }}
            />
            <CardContent sx={{ p: 3 }}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    label="Product name"
                    value={productName}
                    onChange={(e) => setProductName(e.target.value)}
                    fullWidth
                    required
                    error={!!errors.productName}
                    helperText={errors.productName}
                    sx={{ mb: 2 }}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    label="SKU"
                    value={sku}
                    onChange={(e) => setSku(e.target.value)}
                    fullWidth
                    required
                    error={!!errors.sku}
                    helperText={errors.sku}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    select
                    label="Category"
                    value={selectedCategory ?? ''}
                    onChange={(e) => setSelectedCategory(e.target.value ? Number(e.target.value) : null)}
                    fullWidth
                  >
                    {categories.map((category) => (
                      <MenuItem key={category.id ?? 'uncategorized'} value={category.id ?? ''}>
                        {category.name}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <TextField
                      label="New Category"
                      value={customCategory}
                      onChange={(e) => setCustomCategory(e.target.value)}
                      size="small"
                      fullWidth
                    />
                    <Button
                      onClick={handleAddCustomCategory}
                      variant="outlined"
                      disabled={!customCategory.trim()}
                    >
                      Add
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Pricing Card */}
          <Card sx={{ mb: 3, borderRadius: 2 }}>
            <CardHeader
              title="Pricing"
              sx={{
                borderBottom: '1px solid',
                borderColor: 'divider',
                bgcolor: 'action.hover',
              }}
            />
            <CardContent sx={{ p: 3 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Supplier price"
                    type="number"
                    value={supplierPrice}
                    onChange={handleNumericChange(setSupplierPrice)}
                    onFocus={handleNumericFocus(supplierPrice, setSupplierPrice)}
                    onBlur={handleNumericBlur(supplierPrice, setSupplierPrice, 0)}
                    fullWidth
                    required
                    error={!!errors.supplierPrice}
                    helperText={errors.supplierPrice}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    label="Retail price"
                    type="number"
                    value={price}
                    onChange={handleNumericChange(setPrice)}
                    onFocus={handleNumericFocus(price, setPrice)}
                    onBlur={handleNumericBlur(price, setPrice, 0)}
                    fullWidth
                    required
                    error={!!errors.price}
                    helperText={errors.price}
                  />

                </Grid>

                <Grid item xs={12}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                    Wholesale pricing
                  </Typography>
                  <WholesaleBreaksEditor
                    value={wholesaleBreaks}
                    onChange={setWholesaleBreaks}
                    hydrateKey={breaksHydrateKey}
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                    Shown only to approved wholesale customers. Tier discounts apply on top of
                    these prices. A variant with its own wholesale price overrides this.
                  </Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    label="Markup"
                    type="number"
                    value={markup.toFixed(1)}
                    disabled
                    fullWidth
                    InputProps={{
                      endAdornment: <InputAdornment position="end">%</InputAdornment>,
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    label="Margin"
                    type="number"
                    value={margin.toFixed(1)}
                    disabled
                    fullWidth
                    InputProps={{
                      endAdornment: <InputAdornment position="end">%</InputAdornment>,
                    }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    select
                    label="Availability"
                    value={availability}
                    onChange={(e) =>
                      setAvailability(
                        e.target.value as 'both' | 'wholesale_only' | 'retail_only'
                      )
                    }
                    fullWidth
                    helperText="Controls which storefront this product appears in."
                  >
                    <MenuItem value="both">Retail &amp; wholesale</MenuItem>
                    <MenuItem value="retail_only">Retail only</MenuItem>
                    <MenuItem value="wholesale_only">Wholesale only</MenuItem>
                  </TextField>
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    select
                    label="Backorders"
                    value={backorderPolicy}
                    onChange={(e) =>
                      setBackorderPolicy(
                        e.target.value as 'inherit' | 'none' | 'wholesale_only' | 'both'
                      )
                    }
                    fullWidth
                    helperText="Allow ordering past available stock. 'Use store default' follows the global setting; any explicit choice overrides it."
                  >
                    <MenuItem value="inherit">Use store default</MenuItem>
                    <MenuItem value="none">Off (no backorders)</MenuItem>
                    <MenuItem value="wholesale_only">Wholesale only</MenuItem>
                    <MenuItem value="both">Retail &amp; wholesale</MenuItem>
                  </TextField>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Description Card */}
          <Card sx={{ mb: 3, borderRadius: 2 }}>
            <CardHeader
              title="Description"
              sx={{
                borderBottom: '1px solid',
                borderColor: 'divider',
                bgcolor: 'action.hover',
              }}
            />
            <CardContent sx={{ p: 3 }}>
              <ReactQuill
                value={description}
                onChange={setDescription}
                modules={{
                  toolbar: [
                    [{ header: [1, 2, false] }],
                    ['bold', 'italic', 'underline', 'strike'],
                    [{ list: 'ordered' }, { list: 'bullet' }],
                    ['link'],
                    ['clean'],
                  ],
                }}
                theme="snow"
                style={{ height: 200, marginBottom: 50 }}
              />
            </CardContent>
          </Card>

          {/* Main Image Card */}
          <Card sx={{ mb: 3, borderRadius: 2 }}>
            <CardHeader
              title="Main Image"
              sx={{
                borderBottom: '1px solid',
                borderColor: 'divider',
                bgcolor: 'action.hover',
              }}
            />
            <CardContent sx={{ p: 3 }}>
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 3,
                  bgcolor: 'action.hover',
                  borderRadius: 2,
                }}
              >
                {imageUrl ? (
                  <>
                    <Box
                      component="img"
                      src={imageUrl}
                      alt="Product"
                      sx={{
                        width: 80,
                        height: 80,
                        objectFit: 'contain',
                        borderRadius: 1,
                        bgcolor: 'background.paper',
                        p: 1,
                      }}
                    />
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Current image
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        This image will be shown as the main product photo
                      </Typography>
                    </Box>
                    <Button
                      variant="outlined"
                      color="error"
                      onClick={handleMainImageRemove}
                      startIcon={<Delete />}
                    >
                      Remove
                    </Button>
                  </>
                ) : (
                  <>
                    <Box
                      sx={{
                        width: 80,
                        height: 80,
                        bgcolor: 'action.selected',
                        borderRadius: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Image sx={{ color: 'text.secondary', fontSize: 32 }} />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        No image yet
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Upload a high-quality image of your product
                      </Typography>
                    </Box>
                    <Button
                      variant="contained"
                      onClick={() => setMediaOpen(true)}
                    >
                      Choose Image
                    </Button>
                  </>
                )}
              </Paper>
            </CardContent>
          </Card>
        </Grid>

        {/* Right Column - Options & Variants */}
        <Grid item xs={12} lg={4}>
          <Card sx={{ mb: 3, borderRadius: 2, position: 'sticky', top: 24 }}>
            <CardHeader
              title="Product Options"
              subheader="Define variations like size, color, etc."
              sx={{
                borderBottom: '1px solid',
                borderColor: 'divider',
                bgcolor: 'action.hover',
              }}
            />
            <CardContent sx={{ p: 3 }}>
              {options.map((option, index) => (
                <Box key={index}>
                  <Box sx={{ mb: 2 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        gap: 1,
                        mb: 1,
                      }}
                    >
                      <IconButton
                        size="small"
                        onClick={() => moveOption(index, index - 1)}
                        disabled={index === 0}
                      >
                        <ArrowUpward fontSize="small" />
                      </IconButton>

                      <IconButton
                        size="small"
                        onClick={() => moveOption(index, index + 1)}
                        disabled={index === options.length - 1}
                      >
                        <ArrowDownward fontSize="small" />
                      </IconButton>
                    </Box>

                    <OptionInput
                      name={option.name}
                      values={option.values}
                      onNameChange={(newName) => handleOptionNameChange(index, newName)}
                      onValuesChange={(newValues) => handleOptionValuesChange(index, newValues)}
                      onValueRename={(oldVal: string, newVal: string) => {
                        handleOptionValueRename(index, oldVal, newVal);
                      }}
                      onValueDelete={(val: string) => {
                        handleOptionValueDelete(index, val);
                      }}
                      onDelete={() => {
                        handleOptionDelete(index);
                      }}
                      productId={effectiveProductId ?? (isEditMode ? productId : undefined)}
                      valueMeta={optionValueMeta}
                      onSaveValueMeta={handleSaveValueMeta}
                    />
                  </Box>
                </Box>
              ))}

              <Button
                fullWidth
                variant="outlined"
                onClick={() => setOptions([...options, { name: '', values: [] }])}
                startIcon={<Add />}
                sx={{ mt: 1 }}
              >
                Add Option
              </Button>

              {errors.variants && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {errors.variants}
                </Alert>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Variants Section */}
      <Card sx={{ mt: 3, mb: 4, borderRadius: 2 }}>
        <CardHeader
          title={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Variants
              </Typography>
              <Chip
                label={`${variants.length} variant${variants.length !== 1 ? 's' : ''}`}
                size="small"
                color="primary"
              />
            </Box>
          }
          subheader="Manage product variations and inventory"
          sx={{
            borderBottom: '1px solid',
            borderColor: 'divider',
            bgcolor: 'action.hover',
          }}
        />

        {/* Variant Matrix */}
        {options.length === 2 && (
          <>
            <VariantMatrix
              options={options}
              variants={variants}
              baseSku={sku}
              basePrice={Number(price) || 0}
              locations={locations}
              onToggleVariant={toggleVariant}
            />

            {(() => {
              const totalPossible = options[0].values.length * options[1].values.length;
              if (variants.length < totalPossible && variants.length > 0) {
                return (
                  <Alert severity="info" sx={{ mx: 3, mb: 2 }}>
                    New option values added - check the matrix above to create variants for new combinations
                  </Alert>
                );
              }
              return null;
            })()}
          </>
        )}

        {/* Variant Controls */}
        <CardContent sx={{ p: 3 }}>
          {/* Bulk Stock Controls */}
          <Paper
            variant="outlined"
            sx={{
              p: 2.5,
              mb: 3,
              bgcolor: 'action.hover',
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              flexWrap: 'wrap',
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              Bulk stock update
            </Typography>
            <NumericTextField
              label="In-Store"
              size="small"
              value={fillAllInStore}
              onValueChange={setFillAllInStore}
              decimals={0}
              sx={{ width: 120 }}
            />
            <NumericTextField
              label="Online"
              size="small"
              value={fillAllOnline}
              onValueChange={setFillAllOnline}
              decimals={0}
              sx={{ width: 120 }}
            />

            <Button
              variant="contained"
              onClick={handleApplyStockToAllVariants}
              disabled={variants.length === 0}
              sx={{ boxShadow: 'none' }}
            >
              Apply to All
            </Button>
          </Paper>

          {/* Variants List */}
          {variants.length > 0 ? (
            <Box sx={{ mb: 3 }}>
              {variants.map((variant, index) => (
                <VariantCard
                  key={variantKeyFromAttrs(makeAttrs(variant.attributes)) || `${index}`}
                  variant={variant}
                  index={index}
                  allLocations={locations}
                  onUpdate={updateVariant}
                  onDelete={deleteVariant}
                  onImageUpload={handleVariantImageUpload}
                  onImageRemove={handleVariantImageRemove}
                  onOpenMedia={(i) => {
                    setSelectedVariantIndex(i);   // ✅ track which variant
                    setMediaOpen(true);           // ✅ open modal
                  }}
                />
              ))}
            </Box>
          ) : (
            <Paper
              sx={{
                p: 4,
                textAlign: 'center',
                bgcolor: 'action.hover',
                borderRadius: 2,
              }}
            >
              <Inventory sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No variants yet
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Add options above or create a custom variant
              </Typography>
            </Paper>
          )}

          {/* Add Variant Button */}
          {options.length > 0 && (
            <Button
              fullWidth
              variant="outlined"
              startIcon={<Add />}
              onClick={() => setAddVariantModalOpen(true)}
              sx={{
                py: 1.5,
                borderStyle: 'dashed',
                '&:hover': {
                  borderStyle: 'dashed',
                  bgcolor: 'primary.50',
                },
              }}
            >
              Add Custom Variant
            </Button>
          )}
        </CardContent>
      </Card>

      {/* Add Variant Modal */}
      <AddVariantModal
        open={addVariantModalOpen}
        onClose={() => setAddVariantModalOpen(false)}
        options={options}
        existingVariants={variants}
        onSave={upsertVariant}
        baseSku={sku}
      />
      <MediaLibraryModal
        open={mediaOpen}
        onClose={() => setMediaOpen(false)}
        onSelect={(url) => {
          if (selectedVariantIndex !== null) {
            setVariants(prev => {
              const updated = [...prev];
              updated[selectedVariantIndex] = {
                ...prev[selectedVariantIndex],
                imageUrl: url,
                removeImage: false,
                imageFile: undefined,
              };
              return updated;
            });

            setSelectedVariantIndex(null); // ✅ reset
          } else {
            setImageUrl(url);
            setImage(null);
            setRemoveMainImage(false);
          }

          setMediaOpen(false); // ✅ close modal after select
        }}
      />
    </Box>
  );
};

export default ProductForm;